// src/hooks/useFinalStatusQuery.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../services/api.ts';

export interface FinalStatus {
  has_final_video: boolean;
  final_url: string | null;
  status?: string;
  current_step?: string;
}

type Options = {
  shouldPoll?: boolean; // set true after "Assemble" to poll until ready
};

export function useFinalStatusQuery(storyId: number | null, opts: Options = {}) {
  const { shouldPoll = false } = opts;

  return useQuery<FinalStatus, Error>({
    queryKey: ['final-status', storyId],
    queryFn: async () => {
      if (!storyId) return { has_final_video: false, final_url: null };
      const res = await apiGet<FinalStatus>(`/runs/${storyId}/final-status`);
      if (res.error) throw new Error(res.error);
      return res.data ?? { has_final_video: false, final_url: null };
    },
    enabled: !!storyId,
    staleTime: 0,                      // force refetch on focus to refresh pre-signed URLs
    refetchOnWindowFocus: true,        // tab focus → fresh URL
    refetchOnReconnect: true,
    refetchOnMount: 'always',
    placeholderData: (prev) => prev,   // avoid flicker
    gcTime: 5 * 60 * 1000,
    // Poll only while assembling/not ready AND when the tab is visible
    refetchInterval: (data) => {
      const ready = data?.has_final_video && !!data?.final_url;
      if (ready) return false;
      if (!shouldPoll) return false;
      return document.visibilityState === 'visible' ? 5000 : false;
    },
  });
}

export function useAssembleFinalMutation(storyId: number | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!storyId) throw new Error('No storyId');
      const res = await apiPost(`/runs/${storyId}/assemble-final`);
      if (res.error) throw new Error(res.error);
      return res.data;
    },
    onSuccess: async () => {
      // kick a refresh immediately
      await qc.invalidateQueries({ queryKey: ['final-status', storyId] });
    },
  });
}
